"use client";

import { m as motion, useInView, useReducedMotion } from "framer-motion";
import { useRef } from "react";

/**
 * "Esempi di task reali" come task log / feed animato che richiama i
 * "Deploy recenti" dell'hero (coerenza visiva). Righe in stile log: pallino
 * verde, label categoria in mono ciano, titolo, e un id tipo commit a destra.
 * Le righe entrano in cascata allo scroll. Solo transform/opacity.
 */

export interface TaskLogItem {
  title: string;
  type: string;
}

interface TaskLogProps {
  isItalian: boolean;
  items: TaskLogItem[];
}

export function TaskLog({ isItalian, items }: TaskLogProps) {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const active = inView || reduce;

  // id pseudo-commit deterministici per riga (estetica, no random a runtime).
  const hashes = ["a1f9", "7c4e", "e0b2", "3d8a", "9f17", "b62c", "5ad0", "c81f"];

  return (
    <section ref={ref} className="py-20 md:py-28 lg:py-28">
      <div className="container-wide">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={active ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-left mb-10"
        >
          <span className="font-mono text-label text-primary mb-4 block">
            {isItalian ? "Task log" : "Task log"}
          </span>
          <h2 className="font-display text-[2.1rem] sm:text-h2 font-bold mb-4">
            {isItalian ? "Esempi di task reali" : "Real task examples"}
          </h2>
          <p className="text-body-lg text-muted-foreground">
            {isItalian
              ? "Interventi concreti con impatto misurabile."
              : "Concrete interventions with measurable impact."}
          </p>
        </motion.div>

        <div className="min-w-0 max-w-3xl overflow-hidden rounded-2xl border border-[hsl(var(--border))] bg-surface">
          {/* Barra finestra stile terminale */}
          <div className="flex items-center gap-2 border-b border-[hsl(var(--border))] bg-surface-hover/40 px-4 py-2.5">
            <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
            <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
            <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
            <span className="ml-2 font-mono text-[11px] text-muted-foreground/70">
              {isItalian ? "task recenti consegnati" : "recent delivered tasks"}
            </span>
          </div>

          <div className="divide-y divide-[hsl(var(--border))]">
            {items.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, x: -12 }}
                animate={active ? { opacity: 1, x: 0 } : {}}
                transition={reduce ? { duration: 0 } : { duration: 0.35, delay: 0.06 * index }}
                className="group flex items-center gap-3 px-4 py-3 sm:gap-4 sm:px-5"
              >
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[hsl(var(--success))]" />
                <span className="hidden w-32 shrink-0 font-mono text-[11px] uppercase tracking-[0.08em] text-primary sm:block">
                  {item.type}
                </span>
                <div className="min-w-0 flex-1">
                  <span className="mb-0.5 block font-mono text-[10px] uppercase tracking-[0.08em] text-primary sm:hidden">
                    {item.type}
                  </span>
                  <span className="block truncate text-sm font-medium text-foreground/90">
                    {item.title}
                  </span>
                </div>
                <span className="shrink-0 font-mono text-[11px] text-muted-foreground/45">
                  #{hashes[index % hashes.length]}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
